import { useCallback, useContext, useMemo } from 'react';

import { type SessionSummary, type StudentMetricsRow, type StudentSessionMetrics } from './types';
import { DashboardContext } from '../../context/DashboardContext';
import { type ClassSession, type ClassStudent } from '../../context/types';
import { getSortName } from '../../utils/sortingUtils';
import { getDisplayName } from '../../utils/student';
import { type AttendanceData, type SessionAttendanceSummary } from '../attendance/types';

const generateDefaultAverages = () => ({
    classTime: 0,
    talkTime: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    averageTalkTime: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    talkCount: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    feedbackCount: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    attendance: {
        avg: 0,
        max: 0
    },
    raisedHandTime: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    raisedHandCount: {
        teacher: 0,
        assistant: 0,
        student: 0
    }
});

const generateDefaultSummary = () => ({
    classTime: 0,
    maxAttendance: 0,
    talkTime: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    talkCount: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    averageTalkTime: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    raisedHandCount: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    feedbackCount: {
        teacher: 0,
        assistant: 0,
        student: 0
    },
    attendance: {
        teacher: 0,
        assistant: 0,
        student: 0
    }
});

export const useDashboardData = () => {
    const { students, sessions, selectedSessionUuid, sessionMetrics, attendance, classMetrics, classAttendance } =
        useContext(DashboardContext);

    const studentsMap = useMemo(() => {
        const map: { [studentId: string]: ClassStudent } = {};

        students.forEach((student) => {
            map[student.student_id] = student;
        });

        return map;
    }, [students]);

    const attendanceValue = (studentAttendance: AttendanceData): boolean => {
        if (studentAttendance.marks && studentAttendance.marks.marked_present !== null)
            return studentAttendance.marks.marked_present;
        return !!(studentAttendance.events && studentAttendance.events.present);
    };

    const getClassTime = (session: ClassSession) => {
        const startTimeDate = new Date(session.start_time);
        const endTimeDate = new Date(session.end_time);
        return (endTimeDate.getTime() - startTimeDate.getTime()) / 1000;
    };

    const buildStudentMetricsRow = useCallback(
        (student: ClassStudent) => {
            const studentMetrics: StudentSessionMetrics = sessionMetrics[student.student_id];
            const row = {
                studentId: student.student_id,
                role: student.role,
                email: student.email,
                externalId: student.ext_user_id,
                name: getDisplayName(student),
                sortName: getSortName(student),
                talkTime: 0,
                talkCount: 0,
                focusTime: 0,
                focusCount: 0,
                handRaisedTime: 0,
                handRaisedCount: 0,
                feedbackTime: 0,
                feedbackCount: 0,
                starsMax: 0
            };
            if (!studentMetrics) return row;
            row.talkTime = studentMetrics.talk_time;
            row.talkCount = studentMetrics.talk_count;
            row.focusTime = studentMetrics.focus_time;
            row.focusCount = studentMetrics.focus_count;
            row.handRaisedTime = studentMetrics.hand_raised_time;
            row.handRaisedCount = studentMetrics.hand_raised_count;
            row.feedbackTime = studentMetrics.feedback_time;
            row.feedbackCount = studentMetrics.feedback_count;
            row.starsMax = studentMetrics.stars_max;
            return row;
        },
        [sessionMetrics]
    );

    const countMaxStudentsInAttendance = useCallback(
        (attendanceSummary: SessionAttendanceSummary): number => {
            let total = 0;

            Object.keys(attendanceSummary.students).forEach((studentId) => {
                const student = studentsMap[studentId];

                if (student?.role === 'student') {
                    total += 1;
                }
            });

            return total;
        },
        [studentsMap]
    );

    const buildSessionSummary = useCallback(() => {
        const summary = generateDefaultSummary();
        if (!sessionMetrics || !attendance || !sessions.length || !students.length) return summary;

        const [session] = sessions.filter((obj: ClassSession) => obj.session_id === selectedSessionUuid);
        const startTimeDate = new Date(session.start_time);
        const endTimeDate = new Date(session.end_time);
        summary.classTime = (endTimeDate.getTime() - startTimeDate.getTime()) / 1000;

        summary.maxAttendance = countMaxStudentsInAttendance(attendance);

        students.forEach((student) => {
            const { role, student_id: studentId } = student;
            const studentMetric = sessionMetrics[studentId];
            summary.talkTime[role] += studentMetric.talk_time;
            summary.talkCount[role] += studentMetric.talk_count;
            summary.raisedHandCount[role] += studentMetric.hand_raised_count;
            summary.feedbackCount[role] += studentMetric.feedback_count;
            if (attendance.students[studentId]) {
                summary.attendance[role] += attendanceValue(attendance.students[studentId]) ? 1 : 0;
            }
        });

        summary.averageTalkTime.student =
            summary.attendance.student === 0 ? 0 : summary.talkTime.student / summary.attendance.student;

        return summary;
    }, [sessionMetrics, attendance, sessions, students, countMaxStudentsInAttendance, selectedSessionUuid]);

    const buildSemesterAverages = useCallback(() => {
        if (!classMetrics || !classAttendance || !sessions.length || !students.length) return;
        const semesterAverages = generateDefaultAverages();
        sessions.forEach((session: ClassSession) => {
            semesterAverages.classTime += getClassTime(session);
        });
        semesterAverages.classTime /= sessions.length;
        semesterAverages.talkTime.teacher += classMetrics.teachers.talk_time / sessions.length;
        semesterAverages.talkTime.student += classMetrics.students.talk_time / sessions.length;

        classAttendance.forEach((session) => {
            let sessionAttendance = 0;

            Object.keys(session.students).forEach((studentId) => {
                const student = studentsMap[studentId];

                if (student && student?.role === 'student') {
                    sessionAttendance += +attendanceValue(session.students[studentId]);
                }
            });

            semesterAverages.attendance.avg += sessionAttendance;
            semesterAverages.attendance.max += countMaxStudentsInAttendance(session);
        });

        semesterAverages.attendance.avg /= sessions.length;
        semesterAverages.attendance.max /= sessions.length;

        semesterAverages.averageTalkTime.student = semesterAverages.talkTime.student / semesterAverages.attendance.avg;
        semesterAverages.raisedHandCount.student += Math.round(
            classMetrics.students.hand_raised_count / sessions.length
        );
        semesterAverages.feedbackCount.student += Math.round(classMetrics.students.feedback_count / sessions.length);
        return semesterAverages;
    }, [classMetrics, classAttendance, sessions, students.length, countMaxStudentsInAttendance, studentsMap]);

    const studentMetricsRows: StudentMetricsRow[] = useMemo(
        () => (!sessionMetrics || !students.length ? [] : students.map(buildStudentMetricsRow)),
        [buildStudentMetricsRow, students, sessionMetrics]
    );

    const sessionSummary: SessionSummary = useMemo(
        () =>
            !sessionMetrics || !attendance || !selectedSessionUuid ? generateDefaultSummary() : buildSessionSummary(),
        [buildSessionSummary, sessionMetrics, attendance, selectedSessionUuid]
    );

    const classAverages = useMemo(
        () =>
            !classMetrics || !classAttendance || !sessions.length ? generateDefaultAverages() : buildSemesterAverages(),
        [buildSemesterAverages, classMetrics, classAttendance, sessions]
    );
    return { studentMetricsRows, sessionSummary, classAverages };
};
